import React from 'react';
import { Typography } from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { InputRowType } from '../../interfaces';
import InputRow from './InputRow';

interface Props {
  rows: InputRowType[];
  name: string;
}

function dedupeMessages(messages: string[]) {
  return Array.from(new Set(messages));
}

export default function InputGroup({ rows, name }: Props) {
  const { values, setFieldValue, errors, touched, submitCount } =
    useFormikContext<any>();

  const getVisibleError = React.useCallback(
    (fieldName: string) =>
      submitCount > 0 || getIn(touched, fieldName)
        ? getIn(errors, fieldName)
        : undefined,
    [errors, touched, submitCount],
  );

  // Only this group's own errors — a stray error elsewhere in the
  // form must not render an empty error container here
  const hasGroupErrors = React.useMemo(
    () => rows.some(row => row.some(input => !!getVisibleError(input.name))),
    [rows, getVisibleError],
  );

  return (
    <div className="input-group">
      {!!name && <Typography variant="h2">{name}</Typography>}
      <div className="inputs">
        {rows.map((row, index) => (
          <InputRow
            inputs={row}
            values={values}
            setFieldValue={setFieldValue}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            formName={name}
          />
        ))}
      </div>
      {hasGroupErrors && (
        <div className="input-group-errors">
          {rows.map(row =>
            row.map(input => {
              const error = getVisibleError(input.name);
              const messages = Array.isArray(error)
                ? dedupeMessages(error)
                : error;
              if (typeof messages !== 'string' && !Array.isArray(messages)) {
                return null;
              }
              return (
                <div className="error-message" key={`${input.name}-error`}>
                  {messages}
                </div>
              );
            }),
          )}
        </div>
      )}
    </div>
  );
}
